import { cn } from '@/lib/cn'
import type {
  DataTableCardProps,
  DataTableProps,
  DataTableHeadProps,
  DataTableHeadCellProps,
  DataTableBodyProps,
  DataTableRowProps,
  DataTableCellProps,
  DataTableAlign,
} from './types/data-table.types'

export type {
  DataTableCardProps,
  DataTableProps,
  DataTableHeadProps,
  DataTableHeadCellProps,
  DataTableBodyProps,
  DataTableRowProps,
  DataTableCellProps,
  DataTableAlign,
} from './types/data-table.types'

const alignClass: Record<DataTableAlign, string> = {
  left: 'text-left',
  right: 'text-right',
  center: 'text-center',
}

/**
 * The outer border-card shell shared by DataTable and loading skeletons.
 *
 * Use this directly when you need the card wrapper without the <table>
 * element inside — for example when rendering a TableSkeleton.
 *
 * @example
 * // Loading state:
 * <DataTableCard>
 *   <TableSkeleton rows={5} cols={4} />
 * </DataTableCard>
 */
export function DataTableCard({
  children,
  className,
}: DataTableCardProps): React.ReactElement {
  return (
    <div
      className={cn(
        'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800',
        className,
      )}
    >
      {children}
    </div>
  )
}

DataTableCard.displayName = 'DataTableCard'

/**
 * Full data table: card shell + optional overflow scroll + <table> element.
 * Compose with DataTableHead, DataTableHeadCell, DataTableBody, DataTableRow,
 * and DataTableCell.
 *
 * @example
 * <DataTable caption="Roles">
 *   <DataTableHead>
 *     <tr>
 *       <DataTableHeadCell>Name</DataTableHeadCell>
 *       <DataTableHeadCell align="right">Actions</DataTableHeadCell>
 *     </tr>
 *   </DataTableHead>
 *   <DataTableBody>
 *     {rows.map(row => (
 *       <DataTableRow key={row.id}>
 *         <DataTableCell>{row.name}</DataTableCell>
 *         <DataTableCell align="right">…</DataTableCell>
 *       </DataTableRow>
 *     ))}
 *   </DataTableBody>
 * </DataTable>
 */
export function DataTable({
  children,
  scrollable = true,
  className,
  tableClassName,
  caption,
  captionId,
}: DataTableProps): React.ReactElement {
  const table = (
    <table
      className={cn(
        'min-w-full border-collapse text-[13px] leading-5',
        tableClassName,
      )}
    >
      {caption && (
        <caption className="sr-only" id={captionId}>
          {caption}
        </caption>
      )}
      {children}
    </table>
  )

  return (
    <DataTableCard className={className}>
      {scrollable ? (
        <div className="overflow-x-auto">{table}</div>
      ) : (
        table
      )}
    </DataTableCard>
  )
}

DataTable.displayName = 'DataTable'

export function DataTableHead({
  children,
  className,
  sticky = false,
}: DataTableHeadProps): React.ReactElement {
  return (
    <thead
      className={cn(
        'border-b border-gray-200 bg-gray-50/50 dark:border-gray-700 dark:bg-gray-800/80',
        sticky && 'sticky top-0 z-10',
        className,
      )}
    >
      {children}
    </thead>
  )
}

DataTableHead.displayName = 'DataTableHead'

export function DataTableHeadCell({
  children,
  align = 'left',
  className,
  scope = 'col',
  ...rest
}: DataTableHeadCellProps): React.ReactElement {
  return (
    <th
      scope={scope}
      className={cn(
        'px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400',
        alignClass[align],
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  )
}

DataTableHeadCell.displayName = 'DataTableHeadCell'

export function DataTableBody({
  children,
  captionId,
  className,
}: DataTableBodyProps): React.ReactElement {
  return (
    <tbody
      className={cn(
        'divide-y divide-gray-100 dark:divide-gray-700/60',
        className,
      )}
      aria-describedby={captionId}
    >
      {children}
    </tbody>
  )
}

DataTableBody.displayName = 'DataTableBody'

export function DataTableRow({
  children,
  className,
  noHover = false,
  ...rest
}: DataTableRowProps): React.ReactElement {
  return (
    <tr
      className={cn(
        'text-gray-900 transition-colors dark:text-gray-100',
        !noHover && 'hover:bg-gray-50/80 dark:hover:bg-gray-700/40',
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  )
}

DataTableRow.displayName = 'DataTableRow'

export function DataTableCell({
  children,
  align = 'left',
  noWrap = true,
  className,
  ...rest
}: DataTableCellProps): React.ReactElement {
  return (
    <td
      className={cn(
        'px-3 py-2',
        noWrap && 'whitespace-nowrap',
        alignClass[align],
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  )
}

DataTableCell.displayName = 'DataTableCell'
